import React, { useEffect, useMemo, useState } from "react";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { Button, Col, Row } from "../../../../components/Component";
import { Alert, Spinner } from "reactstrap";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  BiBox,
  BiCloudDownload,
  BiGitBranch,
  BiLayer,
  BiLinkExternal,
  BiListUl,
  BiReceipt,
  BiShield,
} from "react-icons/bi";
import TablePageHeading from "../page_heading/TablePageHeading";

const type = "SMART_INVOICE_INVENTORY";

const inventorySections = [
  {
    key: "purchases",
    icon: BiCloudDownload,
    title: "Purchases",
    description: "GRV intake and purchase import flows into the external Smart Invoice stock account.",
    tone: "info",
    colClassName: "col-12 col-xxl-6",
    status: "Pending connection",
    helper: "Bring supplier and GRV imports into the Smart Invoice stock account before downstream invoicing.",
    items: [
      "Non Integrated GRV",
      "Get All Invoices Not Processed",
      "Get GRV By Supplier Tpin",
      "Get Invoice Data",
      "Get All Imports",
      "Get Import By Code",
      "Save Imported",
      "Integrated GRV",
    ],
  },
  {
    key: "items",
    icon: BiBox,
    title: "Items",
    description: "Product creation, validation, and stock adjustments inside the Smart Invoice account.",
    tone: "primary",
    colClassName: "col-12 col-xxl-6",
    status: "Structure ready",
    helper: "Manage catalog creation, stock adjustments, and movement records from the external integration side.",
    items: [
      "Create or Update Product",
      "Create Bulk",
      "See All Products",
      "Validate If Product Exists",
      "Adjust Stock One",
      "Recipe Declare",
      "Record Stock Movement",
      "Validate Bulk",
    ],
  },
  {
    key: "invoice",
    icon: BiReceipt,
    title: "Invoice",
    description: "Invoice posting routes that depend on stock already being available in Smart Invoice.",
    tone: "warning",
    colClassName: "col-12",
    status: "Depends on stock",
    helper: "Invoice route actions stay downstream so stock readiness is visible before invoice submission starts.",
    items: [
      "Invoice Route",
    ],
  },
];

const SmartInvoiceInventory = () => {
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLoading(false);
    }, 900);

    return () => window.clearTimeout(timer);
  }, []);

  const cardMotion = useMemo(
    () => ({
      hidden: { opacity: 0, y: 14, scale: 0.985 },
      show: (i = 0) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.35, delay: i * 0.08, ease: "easeOut" },
      }),
    }),
    [],
  );

  const hoverMotion = useMemo(
    () => ({
      whileHover: { y: -4, transition: { duration: 0.18, ease: "easeOut" } },
      whileTap: { scale: 0.99 },
    }),
    [],
  );

  const overviewCards = useMemo(
    () => [
      {
        key: "modules",
        label: "Inventory Modules",
        value: inventorySections.length,
        tone: "primary",
        icon: BiLayer,
      },
      {
        key: "actions",
        label: "Mapped Actions",
        value: inventorySections.reduce((total, section) => total + section.items.length, 0),
        tone: "info",
        icon: BiListUl,
      },
      {
        key: "state",
        label: "Connection State",
        value: "Scaffolded",
        tone: "warning",
        icon: BiShield,
      },
      {
        key: "dependency",
        label: "Invoice Dependency",
        value: "Stock First",
        tone: "success",
        icon: BiGitBranch,
      },
    ],
    [],
  );

  const toggleSectionExpansion = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const LoadingPanel = () => (
    <div className="card card-bordered shadow-sm">
      <div className="card-inner py-5">
        <div className="d-flex flex-column align-items-center justify-content-center text-center">
          <Spinner />
          <div className="fs-15px mt-3 text-soft">Preparing Smart Invoice inventory panel...</div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Head title="Smart Invoice Inventory" />
      <Content>
        <TablePageHeading title="Smart Invoice Inventory" page_type={type} pageData={[]} />

        {loading ? (
          <LoadingPanel />
        ) : (
          <Row className="g-gs">
            <Col xs="12">
              <div className="card card-bordered shadow-sm">
                <div
                  className="card-inner"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(13,110,253,0.08) 0%, rgba(248,249,250,1) 100%)",
                  }}
                >
                  <div className="row g-3 align-items-start">
                    <div className="col-xl-8">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span
                          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 text-primary"
                          style={{ width: 42, height: 42 }}
                        >
                          <BiLayer className="fs-4" />
                        </span>
                        <span className="text-uppercase small fw-bold text-primary">
                          External Inventory
                        </span>
                      </div>
                      <div className="fs-20px fw-bold">Smart Invoice Stock Workspace</div>
                      <div className="text-soft mt-2" style={{ maxWidth: 720 }}>
                        Manage the external Smart Invoice inventory structure needed before invoice creation.
                        This module is scaffolded and ready for endpoint wiring.
                      </div>
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        <span className="badge bg-primary bg-opacity-75">External stock account</span>
                        <span className="badge bg-info bg-opacity-75">Inventory before invoice</span>
                        <span className="badge bg-light text-dark border">Scaffold mode</span>
                      </div>
                    </div>

                    <div className="col-xl-4">
                      <div className="d-flex flex-wrap gap-2 justify-content-xl-end">
                        <Link to="/dashboard/smart_invoices">
                          <Button color="primary" className="d-flex align-items-center justify-content-center">
                            <BiLinkExternal className="me-1" />
                            Go To Invoices
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col xs="12">
              <Alert color="info" className="mb-0">
                Inventory, product, and invoice-route sections are scaffolded to loading state while external Smart Invoice API integration is pending.
              </Alert>
            </Col>

            {overviewCards.map((card) => {
              const Icon = card.icon;

              return (
                <Col key={card.key} className="col-xxl-3 col-md-6">
                  <motion.div
                    className="card card-bordered shadow-sm h-100"
                    variants={cardMotion}
                    initial="hidden"
                    animate="show"
                    {...hoverMotion}
                    style={{ minWidth: 0, minHeight: 130 }}
                  >
                    <div className="card-inner py-4 d-flex align-items-center h-100">
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <div style={{ minWidth: 0 }}>
                          <div className="small text-soft text-uppercase mb-1">{card.label}</div>
                          <div className="fs-20px fw-bold mt-2" style={{ overflowWrap: "anywhere", lineHeight: 1.2 }}>
                            {card.value}
                          </div>
                        </div>
                        <span
                          className={`d-inline-flex align-items-center justify-content-center rounded-circle bg-${card.tone} bg-opacity-10 text-${card.tone}`}
                          style={{ width: 42, height: 42, flexShrink: 0 }}
                        >
                          <Icon className="fs-4" />
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </Col>
              );
            })}

            {inventorySections.map((section, index) => {
              const Icon = section.icon;
              const isExpanded = Boolean(expandedSections[section.key]);
              const visibleItems = isExpanded ? section.items : section.items.slice(0, 3);
              const hiddenCount = Math.max(section.items.length - visibleItems.length, 0);

              return (
                <Col
                  key={section.key}
                  className={section.colClassName || "col-xl-4 col-lg-6 col-md-12"}
                >
                  <motion.div
                    className="card card-bordered shadow-sm h-100 d-flex flex-column"
                    variants={cardMotion}
                    initial="hidden"
                    animate="show"
                    custom={index}
                    {...hoverMotion}
                    style={{
                      minWidth: 0,
                      minHeight: section.key === "invoice" ? 320 : 560,
                    }}
                  >
                    <div className="card-inner border-bottom">
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <div
                          className={`text-${section.tone} fw-bold pe-2`}
                          style={{ minWidth: 0, lineHeight: 1.35, overflowWrap: "anywhere" }}
                        >
                          {section.title}
                          <div className="small text-soft fw-normal mt-1">{section.status}</div>
                        </div>
                        <span
                          className={`d-inline-flex align-items-center justify-content-center rounded-circle bg-${section.tone} bg-opacity-10 text-${section.tone}`}
                          style={{ width: 42, height: 42, flexShrink: 0 }}
                        >
                          <Icon className="fs-4" />
                        </span>
                      </div>
                    </div>
                    <div className="card-inner d-flex flex-column flex-grow-1">
                      <div
                        className="text-soft mb-3"
                        style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                      >
                        {section.description}
                      </div>
                      <div className="small text-soft border rounded px-3 py-2 mb-3 bg-light">
                        {section.helper}
                      </div>
                      <div className="d-flex align-items-center gap-2 mb-3 small text-soft">
                        <Spinner size="sm" />
                        <span className="fw-medium">Loading state scaffolded</span>
                      </div>
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <div className="small text-soft text-uppercase">Key Actions</div>
                        <div className={`badge bg-${section.tone} bg-opacity-10 text-${section.tone}`}>
                          {section.items.length} total
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {visibleItems.map((item) => (
                          <div
                            key={item}
                            className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded px-3 py-3 bg-light"
                            style={{ minWidth: 0, minHeight: 56 }}
                          >
                            <span
                              className="small flex-grow-1"
                              style={{
                                minWidth: 0,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                                lineHeight: 1.35,
                              }}
                            >
                              {item}
                            </span>
                            <span
                              className={`badge bg-${section.tone} bg-opacity-75 flex-shrink-0`}
                            >
                              Pending
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {(section.items.length > 3 || isExpanded) && (
                      <div className="card-inner border-top bg-lighter">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div className="small text-soft">
                            {isExpanded
                              ? `Showing all ${section.items.length} mapped actions`
                              : `${hiddenCount} more actions available`}
                          </div>
                          <div>
                            <Button
                              color="light"
                              size="sm"
                              className="border"
                              onClick={() => toggleSectionExpansion(section.key)}
                            >
                              {isExpanded ? "Show Less" : "Show More"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </Col>
              );
            })}

            <Col xs="12">
              <div className="card card-bordered shadow-sm">
                <div className="card-inner border-bottom">
                  <div className="fs-16px fw-bold">Feed Preview</div>
                  <div className="text-soft small mt-1">Placeholder area for external stock balances and inventory rows</div>
                </div>
                <div className="card-inner">
                  <div className="border rounded p-5 bg-light text-center">
                    <Spinner size="sm" />
                    <div className="fw-bold mt-3">Inventory records will appear here</div>
                    <div className="text-soft small mt-2">
                      Connect the Smart Invoice inventory endpoints to populate stock balances,
                      product records, purchase imports, and invoice route readiness.
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Content>
    </>
  );
};

export default SmartInvoiceInventory;
